import { ContactSlugEnum, CountryEnum, getContactFieldTitle, setDateSince, setDateTill, setDateWithTimezone, toISOStringWithoutTimezone, useTranslation as useUiTranslation } from '@systemeio/ui';
import ContactFilterWrapper from 'modules/contacts/components/contact-filter-wrapper';
import { ContactFilterAffiliateSearch, ContactFilterContactStateSearch, ContactFilterCountrySearch, ContactFilterDateSearch, ContactFilterDefaultSearch, ContactFilterEmailActivitySearch, ContactFilterTagSearch } from 'modules/contacts/components/contact-filters';
import { contactFilterAdditionalTitles } from 'modules/contacts/constants/contact-filter-titles';
import { ContactFilterConditionEnum, ContactFilterDateConditionEnum, ContactSearchFieldEnum } from 'modules/contacts/enums/contact-enum';
import { ContactFieldApiInterface, ContactFilterContactStateInterface, ContactFilterDateInterface, ContactFilterInterface, ContactFilterTagInterface, ContactFilterTextInterface, ContactFilterType } from 'modules/contacts/types/contact-filter-interface';
import { getDefaultFilterValue } from 'modules/contacts/utils/get-default-filter-value';
import React, { useCallback, useRef } from 'react';
import { FilterableItemType } from 'shared/components/listing-layout';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
const CONTACT_SEARCH_FIELDS_SORT_ORDER: (ContactSlugEnum | ContactSearchFieldEnum)[] = [ContactSlugEnum.Email, ContactSlugEnum.FirstName, ContactSlugEnum.Surname, ContactSlugEnum.StreetAddress, ContactSlugEnum.StreetNumber, ContactSlugEnum.Neighborhood, ContactSlugEnum.Postcode, ContactSlugEnum.City, ContactSlugEnum.State, ContactSlugEnum.Country, ContactSlugEnum.PhoneNumber, ContactSearchFieldEnum.tag, ContactSearchFieldEnum.created_at, ContactSearchFieldEnum.last_activity_date, ContactSearchFieldEnum.state, ContactSlugEnum.CompanyName, ContactSlugEnum.TaxNumber, ContactSearchFieldEnum.affiliate, ContactSearchFieldEnum.removed_at, ContactSearchFieldEnum.removed_reason_type];
interface RenderContactFiltersDataInterface {
  filtersReady: boolean;
  filterFields?: ContactFieldApiInterface;
  setFilter: React.Dispatch<React.SetStateAction<ContactFilterType>>;
  filter: ContactFilterType;
  needToCloseFilters: boolean;
  setNeedToCloseFilters: React.Dispatch<React.SetStateAction<boolean>>;
}
export const useRenderContactFilters = ({
  filtersReady,
  filterFields,
  setFilter,
  needToCloseFilters,
  filter,
  setNeedToCloseFilters
}: RenderContactFiltersDataInterface): FilterableItemType<ContactFilterType>[] => {
  const clearClosingState = useCallback(() => setNeedToCloseFilters(false), [setNeedToCloseFilters]);
  const lastFieldSlug = useRef<ContactSlugEnum | ContactSearchFieldEnum>(undefined);
  const {
    t
  } = useLocoTranslation();
  const {
    t: tUi
  } = useUiTranslation();
  const {
    user
  } = useUser();
  const clearFilterField = useCallback((slug: string) => setFilter(prev => ({
    ...prev,
    [slug]: getDefaultFilterValue(slug)
  })), [setFilter]);
  const addFilter = (slug_0: string) => {
    setFilter(prev_0 => ({
      ...prev_0,
      [slug_0]: [...prev_0[slug_0], ...getDefaultFilterValue(slug_0)]
    }));
  };
  const deleteFilter = (slug_1: string, index: number) => {
    setFilter(prev_1 => {
      prev_1[slug_1].splice(index, 1);
      return {
        ...prev_1,
        [slug_1]: [...prev_1[slug_1]]
      };
    });
  };
  const baseFilters: FilterableItemType<ContactFilterType>[] = filterFields && filtersReady ? filterFields.items.map(filterEl => {
    switch (filterEl.slug) {
      case ContactSlugEnum.Country:
        {
          return {
            field: filterEl.slug,
            onRender: (value_0, onChange_0) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={false} filter={filter} title={getContactFieldTitle(filterEl, tUi)} key={`${filterEl.slug}-filter`} addFilter={addFilter} slug={filterEl.slug} lastElement={lastFieldSlug.current === filterEl.slug}>
                    <>
                      {(value_0 as Array<CountryEnum | undefined>).map((el_0, idx_0) => <ContactFilterCountrySearch index={idx_0} deleteFilter={deleteFilter} length={value_0.length} key={`${filterEl.slug}-filter-${idx_0}`} searchValue={el_0} onChangeSearchValue={data_1 => {
                  const newValue_1: Array<CountryEnum | undefined> = [...(value_0 as Array<CountryEnum | undefined>)];
                  newValue_1[idx_0] = data_1;
                  onChange_0(newValue_1);
                }} slug={ContactSlugEnum.Country} />)}
                    </>
                  </ContactFilterWrapper>
          };
        }
      default:
        {
          return {
            field: filterEl.slug,
            onRender: (value, onChange) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={false} filter={filter} title={getContactFieldTitle(filterEl, tUi)} key={`${filterEl.slug}-${filterEl.fieldName}-filter`} addFilter={addFilter} slug={filterEl.slug} lastElement={lastFieldSlug.current === filterEl.slug}>
                    <>
                      {(value as Array<ContactFilterTextInterface>).map((el, idx) => <ContactFilterDefaultSearch index={idx} deleteFilter={deleteFilter} length={value.length} key={`${filterEl.slug}-filter-${idx}`} searchValue={el.searchValue} onChangeSearchValue={data => {
                  const newValue: ContactFilterTextInterface[] = [...(value as Array<ContactFilterTextInterface>)];
                  newValue[idx] = {
                    searchValue: data,
                    compareOperation: el.compareOperation
                  };
                  onChange(newValue);
                }} slug={filterEl.slug} condition={el.compareOperation} onChangeCondition={data_0 => {
                  const newValue_0 = [...value];
                  newValue_0[idx] = {
                    searchValue: data_0 === ContactFilterConditionEnum.not_set ? '' : el.searchValue,
                    compareOperation: data_0
                  };
                  onChange(newValue_0);
                }} />)}
                    </>
                  </ContactFilterWrapper>
          };
        }
    }
  }) : [];
  const additionalFilters: FilterableItemType<ContactFilterType>[] = [{
    field: ContactSearchFieldEnum.tag,
    onRender: (value_1, onChange_1) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={!filtersReady} filter={filter} title={t(contactFilterAdditionalTitles.search_field_tag)} key={`tag-filter`} addFilter={addFilter} slug={ContactSearchFieldEnum.tag} lastElement={lastFieldSlug.current === ContactSearchFieldEnum.tag}>
          <>
            {(value_1 as Array<ContactFilterTagInterface>).map((el_1, idx_1) => <ContactFilterTagSearch index={idx_1} deleteFilter={deleteFilter} length={value_1.length} key={`tag-filter-${idx_1}`} searchValue={el_1.searchValue ? Number(el_1.searchValue) : undefined} onChangeSearchValue={data_2 => {
          const newValue_2: ContactFilterTagInterface[] = (value_1 as Array<ContactFilterTagInterface>).map((e, i) => {
            if (i === idx_1) {
              return {
                compareOperation: e.compareOperation,
                searchValue: data_2 ? String(data_2) : undefined
              };
            } else {
              return e;
            }
          });
          onChange_1(newValue_2);
        }} slug={ContactSearchFieldEnum.tag} condition={el_1.compareOperation} onChangeCondition={data_3 => {
          const newValue_3: ContactFilterTagInterface[] = (value_1 as Array<ContactFilterTagInterface>).map((e_0, i_0) => {
            if (i_0 === idx_1) {
              return {
                compareOperation: data_3,
                searchValue: undefined
              };
            } else {
              return e_0;
            }
          });
          onChange_1(newValue_3);
        }} />)}
          </>
        </ContactFilterWrapper>
  }, {
    field: ContactSearchFieldEnum.created_at,
    onRender: (value_2, onChange_2) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={!filtersReady} filter={filter} title={t(contactFilterAdditionalTitles.search_field_created_at)} key={`created-at-filter`} addFilter={addFilter} slug={ContactSearchFieldEnum.created_at} lastElement={lastFieldSlug.current === ContactSearchFieldEnum.created_at}>
          <>
            {(value_2 as Array<ContactFilterDateInterface>).map((el_2, idx_2) => <ContactFilterDateSearch index={idx_2} deleteFilter={deleteFilter} length={value_2.length} key={`created-at-filter-${idx_2}`} searchValue={el_2.searchValue} onChangeSearchValue={data_4 => {
          const newValue_4: ContactFilterDateInterface[] = (value_2 as Array<ContactFilterDateInterface>).map((e_1, i_1) => {
            if (i_1 === idx_2) {
              return {
                compareOperation: e_1.compareOperation,
                searchValue: data_4
              };
            } else {
              return e_1;
            }
          });
          onChange_2(newValue_4);
        }} slug={ContactSearchFieldEnum.created_at} condition={el_2.compareOperation} onChangeCondition={data_5 => {
          let date = setDateWithTimezone(el_2.searchValue || undefined, user?.timezone);
          if (data_5 === ContactFilterDateConditionEnum.less_than) {
            date = setDateTill(date);
          } else {
            date = setDateSince(date);
          }
          const newValue_5: ContactFilterDateInterface[] = (value_2 as Array<ContactFilterDateInterface>).map((e_2, i_2) => {
            if (i_2 === idx_2) {
              return {
                compareOperation: data_5,
                searchValue: toISOStringWithoutTimezone(date)
              };
            } else {
              return e_2;
            }
          });
          onChange_2(newValue_5);
        }} />)}
          </>
        </ContactFilterWrapper>
  }, {
    field: ContactSearchFieldEnum.last_activity_date,
    onRender: (value_3, onChange_3) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={!filtersReady} filter={filter} title={t(contactFilterAdditionalTitles.search_field_last_activity_date)} key={`last-activity-date-filter`} addFilter={addFilter} slug={ContactSearchFieldEnum.last_activity_date} lastElement={lastFieldSlug.current === ContactSearchFieldEnum.last_activity_date}>
          <>
            {(value_3 as Array<{
          searchValue: string | null;
        }>).map((el_3, idx_3) => <ContactFilterEmailActivitySearch index={idx_3} deleteFilter={deleteFilter} length={value_3.length} key={`last-activity-date-filter-${idx_3}`} searchValue={el_3.searchValue} onChangeSearchValue={data_6 => {
          const newValue_6: ContactFilterInterface['last_activity_date'] = (value_3 as ContactFilterInterface['last_activity_date']).map((e_3, i_3) => {
            if (i_3 === idx_3) {
              return {
                searchValue: data_6
              };
            } else {
              return e_3;
            }
          });
          onChange_3(newValue_6);
        }} slug={ContactSearchFieldEnum.last_activity_date} />)}
          </>
        </ContactFilterWrapper>
  }, {
    field: ContactSearchFieldEnum.state,
    onRender: (value_4, onChange_4) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={!filtersReady} filter={filter} title={t(contactFilterAdditionalTitles.search_field_state)} key={`contact-state-filter`} addFilter={addFilter} slug={ContactSearchFieldEnum.state} lastElement={lastFieldSlug.current === ContactSearchFieldEnum.state}>
          <>
            {(value_4 as Array<ContactFilterContactStateInterface>).map((el_4, idx_4) => <ContactFilterContactStateSearch index={idx_4} deleteFilter={deleteFilter} length={value_4.length} key={`state-filter-${idx_4}`} condition={el_4.searchValue} onChangeCondition={data_7 => {
          const newValue_7: ContactFilterContactStateInterface[] = (value_4 as Array<ContactFilterContactStateInterface>).map((e_4, i_4) => {
            if (i_4 === idx_4) {
              return {
                searchValue: data_7
              };
            } else {
              return e_4;
            }
          });
          onChange_4(newValue_7);
        }} slug={ContactSearchFieldEnum.state} />)}
          </>
        </ContactFilterWrapper>
  }, {
    field: ContactSearchFieldEnum.affiliate,
    onRender: (value_5, onChange_5) => <ContactFilterWrapper clearClosingState={clearClosingState} needToCloseFilters={needToCloseFilters} clearFilterField={clearFilterField} isFetching={!filtersReady} filter={filter} title={t(contactFilterAdditionalTitles.search_field_affiliate)} key={`affiliate-filter`} addFilter={addFilter} slug={ContactSearchFieldEnum.affiliate} lastElement={lastFieldSlug.current === ContactSearchFieldEnum.affiliate}>
          <>
            {(value_5 as Array<{
          searchValue: string;
        }>).map((el_5, idx_5) => <ContactFilterAffiliateSearch index={idx_5} deleteFilter={deleteFilter} length={value_5.length} key={`affiliate-filter-${idx_5}`} searchValue={el_5.searchValue} onChangeSearchValue={data_8 => {
          const newValue_8: ContactFilterInterface['affiliate'] = (value_5 as Array<{
            searchValue: string;
          }>).map((e_5, i_5) => {
            if (i_5 === idx_5) {
              return {
                searchValue: data_8
              };
            } else {
              return e_5;
            }
          });
          onChange_5(newValue_8);
        }} slug={ContactSearchFieldEnum.affiliate} />)}
          </>
        </ContactFilterWrapper>
  }];
  const sortedFields = [...baseFilters, ...additionalFilters].sort((a, b) => {
    const indexA = CONTACT_SEARCH_FIELDS_SORT_ORDER.indexOf(a.field as ContactSlugEnum | ContactSearchFieldEnum);
    const indexB = CONTACT_SEARCH_FIELDS_SORT_ORDER.indexOf(b.field as ContactSlugEnum | ContactSearchFieldEnum);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
  lastFieldSlug.current = sortedFields[sortedFields.length - 1]?.field as ContactSlugEnum | ContactSearchFieldEnum;
  return sortedFields;
};